import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Footer, MarketingHeader, ProductFactBlurb, SEO } from '@/components';
import { ChildSupportCalculator } from '@/components/child-support/ChildSupportCalculator';
import { track } from '@/lib/analytics';
import { CHILD_SUPPORT_STATES } from '@/lib/child-support/schemas';

const sortedStates = [...CHILD_SUPPORT_STATES].sort((a, b) => a.name.localeCompare(b.name));

function ChildSupportHubPage() {
  useEffect(() => {
    track('Child Support Hub View');
  }, []);

  const appJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Harbor Child Support Calculator',
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    url: 'https://harborcoparent.com/child-support-calculator',
  };

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Child Support Calculator by State (2026) | Harbor'
        description='Free child support calculator for all 50 states and DC. Get a quick monthly estimate based on income and parenting time. Educational only, not legal advice.'
        path='/child-support-calculator'
        jsonLd={appJsonLd}
      />
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          <h1 className='mb-4 text-4xl font-bold text-gray-900 md:text-5xl'>
            Child support calculator by state
          </h1>
          <p className='mx-auto max-w-2xl text-xl leading-relaxed text-gray-600'>
            Estimate monthly child support for any US state with quick inputs. Results are
            educational estimates only and not legal advice. Always consult a family law
            professional for your situation.
          </p>
          <ProductFactBlurb />
        </div>
      </section>

      <div className='mx-auto max-w-7xl px-6 py-12'>
        <ChildSupportCalculator />

        <section className='mt-16'>
          <h2 className='mb-2 text-2xl font-bold text-gray-900'>All state calculators</h2>
          <p className='mb-6 text-gray-600'>
            Select your state for a localized child support estimate.
          </p>
          <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'>
            {sortedStates.map((state) => (
              <Link
                key={state.slug}
                to='/child-support-calculator/$state'
                params={{ state: state.slug }}
                onClick={() => track('Click Child Support State Link', { state: state.slug })}
                className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800 hover:border-primary hover:text-primary'
              >
                {state.name}
              </Link>
            ))}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/child-support-calculator/')({
  component: ChildSupportHubPage,
});
